import { motion } from "framer-motion";
import { Plus } from "lucide-react";

const foundingLeaders = [
  {
    name: "Arvind Jain",
    role: "Managing Director",
    bio: "40+ years experience in Production, Planning, and Finance.",
    image: "images/team/arvind-jain.jpg",
  },
  {
    name: "Subodh Jain",
    role: "Managing Director",
    bio: "40+ years guiding strategic vision and operations.",
    image: "images/team/subodh-jain.jpg",
  },
];

const mumbaiLeaders = [
  {
    name: "Laveena Jain",
    role: "Partner",
    bio: "M-Arch from Cardiff University, 10+ years in design & build.",
    image: "images/team/laveena-jain.jpg",
  },
  {
    name: "Harsh Bhartiya",
    role: "Partner",
    bio: "B.E. & MBA, ex-Reliance Group experience in large-scale projects.",
    image: "images/team/harsh-bhartiya.jpg",
  },
];

// Sanjay Jain, Rahul Verma, Mayank Mittal
const teamMembers = [
  {
    name: "Sanjay Jain",
    bio: "20+ years in operations, ensuring seamless project execution.",
    image: "images/team/sanjay-jain.jpg",
  },
  {
    name: "Rahul Verma",
    image: "images/team/rahul-verma.jpg",
  },
  {
    name: "Mayank Mittal",
    image: "images/team/mayank-mittal.jpg",
  },
];

const certifications = [
  "ISO 9001",
  "ISO 14001",
  "ISO 45001",
  "NSC Certificate",
  "Airport Security Clearance",
];

export const Leadership = () => {
  return (
    <>
      <section className="bg-neutral-50 py-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-6">
            <div>
              <p className="text-primary font-medium tracking-widest uppercase mb-4">
                Our Leadership
              </p>
              <h2 className="font-serif text-4xl md:text-5xl text-gray-900">
                Visionaries at the Helm
              </h2>
            </div>
            <p className="max-w-md text-gray-500 font-light text-right md:text-left">
              Guided by decades of experience and a passion for engineering
              excellence.
            </p>
          </div>

          <div className="max-w-5xl mx-auto">
            <p className="text-primary text-xs font-semibold tracking-[0.35em] uppercase mb-6 text-center md:text-left">
              The Founding Vision
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-12">
              {foundingLeaders.map((leader, index) => (
                <motion.div
                  key={leader.name}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.12, duration: 0.6 }}
                  className="group cursor-pointer md:scale-[1.02]">
                  <div className="relative aspect-3/4 overflow-hidden mb-6 bg-gray-200">
                    <img
                      src={leader.image}
                      alt={leader.name}
                      className="w-full h-full object-cover object-top filter grayscale group-hover:grayscale-0 transition-all duration-700 ease-in-out group-hover:scale-105"
                    />

                    <div className="absolute top-4 right-4 bg-white/10 backdrop-blur-md p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <Plus className="w-5 h-5 text-white" />
                    </div>
                  </div>

                  <div className="flex flex-col items-start border-l-2 border-transparent group-hover:border-primary pl-0 group-hover:pl-4 transition-all duration-300">
                    <h3 className="font-serif text-2xl font-bold text-gray-900 group-hover:text-black transition-colors">
                      {leader.name}
                    </h3>
                    <span className="text-primary text-xs font-bold uppercase tracking-widest mt-1">
                      {leader.role}
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          <div className="py-12 max-w-4xl mx-auto space-y-8">
            <p className="text-primary text-xs font-semibold tracking-[0.35em] uppercase mb-6 text-center md:text-left">
              Our Team
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {mumbaiLeaders.map((leader, index) => (
                <motion.div
                  key={leader.name}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.12, duration: 0.6 }}
                  className="group cursor-pointer">
                  <div className="relative aspect-3/4 overflow-hidden mb-6 bg-gray-200">
                    <img
                      src={leader.image}
                      alt={leader.name}
                      className="w-full h-full object-cover object-top filter grayscale group-hover:grayscale-0 transition-all duration-700 ease-in-out group-hover:scale-105"
                    />

                    <div className="absolute top-4 right-4 bg-white/10 backdrop-blur-md p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <Plus className="w-5 h-5 text-white" />
                    </div>
                  </div>

                  <div className="flex flex-col items-start border-l-2 border-transparent group-hover:border-primary pl-0 group-hover:pl-4 transition-all duration-300">
                    <h3 className="font-serif text-2xl font-bold text-gray-900 group-hover:text-black transition-colors">
                      {leader.name}
                    </h3>
                    <span className="text-primary text-xs font-bold uppercase tracking-widest mt-1">
                      {leader.role}
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {teamMembers.map((leader, index) => (
                <motion.div
                  key={leader.name}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.12, duration: 0.6 }}
                  className="group cursor-pointer">
                  <div className="relative aspect-3/4 overflow-hidden mb-6 bg-gray-200">
                    <img
                      src={leader.image}
                      alt={leader.name}
                      className="w-full h-full object-cover object-top filter grayscale group-hover:grayscale-0 transition-all duration-700 ease-in-out group-hover:scale-105"
                    />

                    <div className="absolute top-4 right-4 bg-white/10 backdrop-blur-md p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <Plus className="w-5 h-5 text-white" />
                    </div>
                  </div>

                  <div className="flex flex-col items-start border-l-2 border-transparent group-hover:border-primary pl-0 group-hover:pl-4 transition-all duration-300">
                    <h3 className="font-serif text-2xl font-bold text-gray-900 group-hover:text-black transition-colors">
                      {leader.name}
                    </h3>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-neutral-900 py-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <p className="text-primary text-xs font-semibold tracking-[0.35em] uppercase mb-8">
            Certifications & Compliance
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="flex flex-wrap gap-4">
              {certifications.map((certification, index) => (
                <motion.div
                  key={certification}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.08, duration: 0.5 }}
                  className="px-4 py-3 min-w-35 text-center border border-[#d6b46a] text-[#f5e6c1] uppercase tracking-[0.2em] text-[11px] bg-white/5 backdrop-blur-md">
                  {certification}
                </motion.div>
              ))}
            </div>
            <div className="border-l-2 border-primary pl-6">
              <h3 className="font-serif text-2xl md:text-3xl text-white mb-4">
                Licensed for Critical Zones.
              </h3>
              <p className="text-neutral-300 leading-relaxed">
                Arihant Infracon holds valid airport security clearance and is
                authorized to deploy manpower and execute works in airside,
                terminal (SHA/Non-SHA) and landside areas of operational
                airports, in compliance with applicable BCAS and airport
                security requirements.
              </p>
              <p className="text-neutral-500 text-xs italic mt-6">
                Disclaimer: Security clearance documents are shared only with
                authorized clients and authorities upon request.
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};
